(function(){
'use strict';
/*
 * Composants repris de Shrine, reecrits en clair pour Refresh.
 * Aucune librairie externe. Tout est pilote par les data-attributs
 * generes depuis les reglages de section : rien n'est code en dur.
 *
 *  - <copy-button>      copie un selecteur dans le presse-papier (outil d'edition)
 *  - <internal-video>   lecteur video avec bouton play et autoplay optionnel
 *  - <splide-component> carrousel compatible avec le balisage .splide__* de Shrine
 */

/* ------------------------------------------------------------------ *
 * copy-button
 * ------------------------------------------------------------------ */
class CopyButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => this.copy());
  }

  async copy() {
    const value = this.dataset.content || '';
    try {
      await navigator.clipboard.writeText(value);
      this.dataset.success = 'true';
      setTimeout(() => (this.dataset.success = 'false'), 2000);
    } catch (e) {
      this.dataset.success = 'false';
    }
  }
}
if (!customElements.get('copy-button')) customElements.define('copy-button', CopyButton);

/* ------------------------------------------------------------------ *
 * internal-video
 * ------------------------------------------------------------------ */
class InternalVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.trigger = this.querySelector('[data-play-btn], .video-play-btn');
    if (!this.video) return;

    this.autoplay = this.dataset.autoplay === 'true';

    if (this.autoplay) {
      this.video.muted = true;
      this.video.setAttribute('playsinline', '');
      this.observe();
    }
    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.toggle());
    }
    this.video.addEventListener('play', () => this.classList.add('is-playing'));
    this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
  }

  /* ne lance la lecture que lorsque la video entre dans le viewport */
  observe() {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.video.play().catch(() => {});
          else this.video.pause();
        });
      },
      { threshold: 0.25 }
    );
    io.observe(this);
  }

  toggle() {
    if (this.video.paused) this.video.play().catch(() => {});
    else this.video.pause();
  }

  pause() {
    if (this.video && !this.video.paused) this.video.pause();
  }
}
if (!customElements.get('internal-video')) customElements.define('internal-video', InternalVideo);

/* ------------------------------------------------------------------ *
 * splide-component
 * ------------------------------------------------------------------ */
const SPLIDE_BREAKPOINT = 750; // aligne sur la mediaquery de Dawn/Refresh

class SplideComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.splide__track');
    this.list = this.querySelector('.splide__list');
    if (!this.track || !this.list) return;

    this.slides = Array.from(this.list.querySelectorAll('.splide__slide'));
    if (!this.slides.length) return;

    this.index = 0;
    this.type = this.dataset.type || 'slide';
    this.isFade = this.type === 'fade';
    this.isLoop = this.type === 'loop';

    this.prevBtn = this.querySelector('.splide__arrow--prev');
    this.nextBtn = this.querySelector('.splide__arrow--next');
    this.pagination = this.querySelector('.splide__pagination');

    this.bind();
    this.refresh();

    this.resizeHandler = () => this.refresh();
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    if (this.dataset.autoplay === 'true') this.startAutoplay();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeHandler);
    this.stopAutoplay();
  }

  /* --- lecture des reglages selon la taille d'ecran --- */
  get isMobile() {
    return window.innerWidth < SPLIDE_BREAKPOINT;
  }

  get disabled() {
    const key = this.isMobile ? 'destroyMobile' : 'destroyDesktop';
    return this.dataset[key] === 'true';
  }

  setting(name, fallback) {
    const suffix = this.isMobile ? 'Mobile' : 'Desktop';
    const value = this.dataset[name + suffix];
    if (value === undefined || value === '') return fallback;
    const num = parseFloat(value);
    return Number.isNaN(num) ? value : num;
  }

  get perView() {
    return Math.max(1, this.setting('slides', 1));
  }

  get perMove() {
    return Math.max(1, this.setting('perMove', 1));
  }

  get gap() {
    return this.setting('gap', 0);
  }

  get sidePadding() {
    return this.setting('sidePadding', 0);
  }

  get maxIndex() {
    if (this.isLoop) return this.slides.length - 1;
    if (this.dataset.omitEnd === 'true') {
      return Math.max(0, this.slides.length - this.perView);
    }
    return this.slides.length - 1;
  }

  /* --- mise en page --- */
  refresh() {
    this.classList.toggle('splide--inactive', this.disabled);
    this.classList.toggle('splide--loop', this.isLoop);

    if (this.disabled) {
      this.list.style.cssText = '';
      this.slides.forEach((s) => (s.style.cssText = ''));
      return;
    }

    const gap = this.gap;
    const pad = this.sidePadding;
    const width = `calc((100% - ${gap * (this.perView - 1)}px) / ${this.perView})`;

    this.track.style.paddingLeft = pad + 'px';
    this.track.style.paddingRight = pad + 'px';
    this.track.style.overflow = 'hidden';

    if (this.isFade) {
      this.list.style.display = 'grid';
      this.slides.forEach((slide, i) => {
        slide.style.gridArea = '1 / 1';
        slide.style.opacity = i === this.index ? '1' : '0';
        slide.style.transition = 'opacity var(--duration-default, .25s) ease';
        slide.style.pointerEvents = i === this.index ? 'auto' : 'none';
      });
    } else {
      this.list.style.display = 'flex';
      this.list.style.gap = gap + 'px';
      this.list.style.transition = 'transform var(--duration-default, .25s) ease';
      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${width}`;
        slide.style.maxWidth = width;
      });
    }

    this.index = Math.min(this.index, this.maxIndex);
    this.go(this.index, false);
    this.buildPagination();
    this.adaptHeight();
  }

  adaptHeight() {
    const key = this.isMobile ? 'mobileAdaptiveHeight' : 'desktopAdaptiveHeight';
    if (this.dataset[key] !== 'true') {
      this.list.style.alignItems = '';
      return;
    }
    this.list.style.alignItems = 'flex-start';
  }

  /* --- navigation --- */
  go(index, animate = true) {
    const max = this.maxIndex;
    if (this.isLoop) {
      if (index < 0) index = max;
      if (index > max) index = 0;
    } else {
      index = Math.max(0, Math.min(index, max));
    }
    this.index = index;

    if (!animate) this.list.style.transition = 'none';

    if (this.isFade) {
      this.slides.forEach((slide, i) => {
        slide.style.opacity = i === index ? '1' : '0';
        slide.style.pointerEvents = i === index ? 'auto' : 'none';
      });
    } else {
      const slide = this.slides[index];
      const offset = slide ? slide.offsetLeft - this.slides[0].offsetLeft : 0;
      this.list.style.transform = `translateX(${-offset}px)`;
    }

    if (!animate) {
      // force le reflow avant de retablir la transition
      void this.list.offsetHeight;
      this.list.style.transition = '';
    }

    this.updateControls();
    this.pauseVideosOutside();
  }

  next() {
    this.go(this.index + this.perMove);
  }

  prev() {
    this.go(this.index - this.perMove);
  }

  updateControls() {
    if (this.prevBtn && !this.isLoop) this.prevBtn.disabled = this.index <= 0;
    if (this.nextBtn && !this.isLoop) this.nextBtn.disabled = this.index >= this.maxIndex;

    if (!this.pagination) return;
    Array.from(this.pagination.children).forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      const btn = dot.querySelector('button') || dot;
      btn.setAttribute('aria-current', i === this.index ? 'true' : 'false');
    });
  }

  buildPagination() {
    if (!this.pagination) return;
    const count = this.maxIndex + 1;
    if (this.pagination.children.length === count) return this.updateControls();

    this.pagination.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const li = document.createElement('li');
      li.className = 'splide__pagination__page';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', `${i + 1}`);
      btn.addEventListener('click', () => {
        this.go(i);
        this.restartAutoplay();
      });
      li.appendChild(btn);
      this.pagination.appendChild(li);
    }
    this.updateControls();
  }

  pauseVideosOutside() {
    if (this.dataset.pauseVideos !== 'true') return;
    this.slides.forEach((slide, i) => {
      if (i === this.index) return;
      slide.querySelectorAll('internal-video').forEach((v) => v.pause && v.pause());
      slide.querySelectorAll('video').forEach((v) => !v.paused && v.pause());
    });
  }

  /* --- interactions --- */
  bind() {
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.next(); this.restartAutoplay(); });
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.prev(); this.restartAutoplay(); });

    if (this.dataset.drag !== 'false' && !this.isFade) this.bindDrag();

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => { if (this.dataset.autoplay === 'true') this.startAutoplay(); });
  }

  bindDrag() {
    let startX = 0;
    let dragging = false;

    const down = (e) => {
      if (this.disabled) return;
      dragging = true;
      startX = (e.touches ? e.touches[0].clientX : e.clientX);
      this.list.style.transition = 'none';
    };
    const move = (e) => {
      if (!dragging) return;
      const x = (e.touches ? e.touches[0].clientX : e.clientX);
      if (Math.abs(x - startX) > 10 && e.cancelable) e.preventDefault();
    };
    const up = (e) => {
      if (!dragging) return;
      dragging = false;
      this.list.style.transition = '';
      const x = (e.changedTouches ? e.changedTouches[0].clientX : e.clientX);
      const delta = x - startX;
      if (Math.abs(delta) > 40) delta < 0 ? this.next() : this.prev();
      else this.go(this.index, false);
      this.restartAutoplay();
    };

    this.addEventListener('mousedown', down);
    this.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('mousemove', move);
    this.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', up);
    this.addEventListener('touchend', up);
  }

  /* --- autoplay --- */
  startAutoplay() {
    this.stopAutoplay();
    /* le reglage de section est en SECONDES (unit: "sec"), pas en millisecondes */
    const raw = parseFloat(this.dataset.autoplaySpeed);
    const seconds = Number.isFinite(raw) && raw > 0 ? raw : 5;
    const speed = Math.max(1000, seconds * 1000);
    this.timer = setInterval(() => this.next(), speed);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    if (this.dataset.autoplay === 'true') this.startAutoplay();
  }
}
if (!customElements.get('splide-component')) customElements.define('splide-component', SplideComponent);


/* ------------------------------------------------------------------ *
 * Ouverture du tiroir panier depuis l'icone du header Shrine.
 * Le header de Shrine pointe simplement vers /cart ; on intercepte le
 * clic pour ouvrir <cart-drawer> quand il est disponible. Si le tiroir
 * n'existe pas ou n'est pas pret, on laisse le lien faire son travail :
 * l'utilisateur atterrit sur la page panier, jamais dans le vide.
 * ------------------------------------------------------------------ */
function bindCartDrawer() {
  const icon = document.getElementById('cart-icon-bubble');
  if (!icon || icon.dataset.drawerBound === 'true') return;
  icon.dataset.drawerBound = 'true';

  icon.addEventListener('click', (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return; // repli : navigation normale
    if (drawer.classList.contains('is-empty')) return;
    event.preventDefault();
    drawer.open(icon);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindCartDrawer);
} else {
  bindCartDrawer();
}
document.addEventListener('shopify:section:load', bindCartDrawer);


/* ------------------------------------------------------------------ *
 * <sticky-header> — en-tete collant
 * Shrine declare cet element dans son bundle minifie, absent de Refresh.
 * Sans lui l'en-tete reste en position statique et le contenu de la page
 * defile par-dessus. Reimplementation conforme au contrat CSS du theme :
 * classes .shopify-section-header-sticky / -hidden / .animate et variable
 * --header-height.
 * ------------------------------------------------------------------ */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('.section-header') || this.closest('.shopify-section');
    if (!this.header) return;

    this.headerBounds = {};
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
    this.setHeaderHeight();
    window.addEventListener('resize', () => this.setHeaderHeight(), { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty(
      '--header-height',
      `${this.header.offsetHeight}px`
    );
  }

  /* memorise la hauteur de l'en-tete des qu'il sort du viewport */
  createObserver() {
    const observer = new IntersectionObserver((entries, obs) => {
      this.headerBounds = entries[0].intersectionRect;
      obs.disconnect();
    });
    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => (this.preventReveal = false), 66);
        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    this.header.classList.remove(
      'shopify-section-header-hidden',
      'animate',
      'shopify-section-header-sticky'
    );
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((d) => d.close && d.close());
  }

  closeSearchModal() {
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (this.searchModal && this.searchModal.close) this.searchModal.close(false);
  }
}
if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

})();
